"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDayMonthYearNameWithCommas = exports.getDateAndTimeDetail = exports.getDateAndTimeFilterFormat = exports.getTimeInHourMinuteFormat = exports.getTimeStampWithoutSeconds = exports.serverTimeStampToDate = exports.toServerTimeStamp = exports.toZonedFormat = exports.fixTimeZone = exports.replaceTimeZone = exports.guessTimezoneNameFromISODateOROffset = exports.getTimeZoneFromDateTimeStamp = exports.rangeValidator = exports.validateFormat = exports.parsePGDateTimetzToDate = exports.parsePGTimeToDate = exports.parsePGTimetzToDate = exports.formatDateToPGTimetz = exports.formatDateToPGDate = exports.formatDateToISOString = exports.TIMEZONES = void 0;
const date_fns_1 = require("date-fns");
const date_fns_tz_1 = require("date-fns-tz");
const lodash_1 = require("lodash");
const timezone_support_1 = require("timezone-support");
const types_1 = require("../types");
exports.TIMEZONES = Object.freeze((0, timezone_support_1.listTimeZones)());
const formatDateToISOString = (date) => {
    return (0, date_fns_1.formatISO)(date, { format: "extended" });
};
exports.formatDateToISOString = formatDateToISOString;
/**
 * format JS Date obj to DateFormats.short
 * Ex. "2021-01-04"
 */
const formatDateToPGDate = (date) => {
    return (0, date_fns_1.format)(date, types_1.DateFormats.short);
};
exports.formatDateToPGDate = formatDateToPGDate;
/**
 * format JS Date obj to DateFormats.time
 * Ex. "15:00:00+00:00"
 */
const formatDateToPGTimetz = (date) => {
    return (0, date_fns_1.format)(date, types_1.DateFormats.time);
};
exports.formatDateToPGTimetz = formatDateToPGTimetz;
/**
 * parse DateFormats.time to JS Date obj
 */
function parsePGTimetzToDate(dateString) {
    return (0, date_fns_1.parse)(dateString, types_1.DateFormats.time, new Date());
}
exports.parsePGTimetzToDate = parsePGTimetzToDate;
function parsePGTimeToDate(dateString) {
    return (0, date_fns_1.parse)(dateString, types_1.DateFormats.timeWithoutTz, new Date());
}
exports.parsePGTimeToDate = parsePGTimeToDate;
/**
 * parse DateFormats.long to JS Date obj
 */
function parsePGDateTimetzToDate(dateString) {
    return (0, date_fns_1.parse)(dateString, types_1.DateFormats.datetime, new Date());
}
exports.parsePGDateTimetzToDate = parsePGDateTimetzToDate;
/**
 * @description
 * Utility Method for validating the format of the dateTime string.
 * @param {string} dateTimeString. e.g 2020-01-01
 * @param {DateFormats} expectedFormat. e.g yyyy-MM-dd, HH:mm:ss
 * @param {string} variableName. e.g startTime. For formatting the message.
 * @returns {boolean}
 * */
function validateFormat(dateTimeString, expectedFormat, variableName) {
    if (!(0, date_fns_1.isMatch)(dateTimeString, expectedFormat)) {
        throw new types_1.InvalidDateTimeFormat(expectedFormat, variableName);
    }
    return true;
}
exports.validateFormat = validateFormat;
/**
 * @description
 * Validate the range.
 * @param {DateFormats} rangeFormat.
 * @param {IRangeValidatorInput}.
 * @param {IRangeValidatorOptions}.
 * @returns {boolean}
 * */
function rangeValidator(rangeFormat, { start, end }, { canBeSame = true, checkPast = true, canStartToday = true, } = {}) {
    const type = rangeFormat === types_1.DateFormats.time ? "time" : "date";
    validateFormat(start, rangeFormat, `Start ${type}`);
    validateFormat(end, rangeFormat, `End ${type}`);
    let rightNow = (0, date_fns_1.parse)((0, date_fns_1.format)(new Date(), rangeFormat), rangeFormat, new Date());
    rightNow = canStartToday ? (0, date_fns_1.startOfDay)((0, date_fns_1.subDays)(rightNow, 1)) : rightNow;
    const startDateObject = (0, date_fns_1.parse)(start, rangeFormat, new Date());
    const endDateObject = (0, date_fns_1.parse)(end, rangeFormat, new Date());
    if (checkPast &&
        ((canStartToday && !(startDateObject >= rightNow)) ||
            (!canStartToday && startDateObject < rightNow))) {
        throw new types_1.InvalidRangeError(`Start ${type} must not be a past date.`);
    }
    const diff = (0, date_fns_1.differenceInSeconds)(endDateObject, startDateObject);
    if (diff < 0 || (diff === 0 && !canBeSame)) {
        throw new types_1.InvalidRangeError(`Start ${type} should be less than End ${type}`);
    }
    return true;
}
exports.rangeValidator = rangeValidator;
/**
 * @description
 * Extract the timezone from timestamp.
 * @param {string} stamp. Date Stamp 10:00:00+07, 2020-10-10T10:00:00+07, 2020-10-10T10:00:00+07:00
 * @returns {string | null} String +07, 00+07
 * */
function getTimeZoneFromDateTimeStamp(stamp) {
    const withOutDate = stamp.replace(types_1.SHORT_DATE_REGEX, "");
    const matchArray = withOutDate.match(types_1.TIMEZONE_OFFSET_REGEX);
    return Array.isArray(matchArray) ? matchArray[0] : matchArray;
}
exports.getTimeZoneFromDateTimeStamp = getTimeZoneFromDateTimeStamp;
/**
 * @description
 * Guess the timezone Name from offset
 * @param {string} dateOrOffset. 2020-10-10T10:00:00+07, +08:00
 * @return {string} Name of Timezone. Asia/Karachi, Asia/Bangkok
 * */
function guessTimezoneNameFromISODateOROffset(dateOrOffset) {
    const offset = getTimeZoneFromDateTimeStamp(dateOrOffset);
    if (!offset) {
        throw new types_1.InvalidDateTimeFormat(`${types_1.DateFormats.long} OR ${types_1.DateFormats.time} OR (+/-)00:00`, dateOrOffset);
    }
    const zone = (0, lodash_1.find)(exports.TIMEZONES, (timeZone) => offset === (0, date_fns_tz_1.format)(new Date(), "xxx", { timeZone }));
    if (!zone)
        throw new types_1.InvalidOffset(offset);
    return zone;
}
exports.guessTimezoneNameFromISODateOROffset = guessTimezoneNameFromISODateOROffset;
/**
 * @description
 * Replace the timezone part with provided string
 * @param {string} timeStamp. 2020-10-10T10:00:00+07 OR 10:00:00+07
 * @param {string} withStr. Value to replace timezone with. Default "Z"
 * @returns {string}
 * */
function replaceTimeZone(timeStamp, withStr = "Z") {
    return timeStamp.replace(types_1.TIMEZONE_OFFSET_REGEX, withStr);
}
exports.replaceTimeZone = replaceTimeZone;
/**
 * @description
 * Method for postfixing the last zeros in the time stamp
 * @param {string} date. e.g 2020-01-01T12:00:00+07, 12:00:00+07
 * @returns {string} 2020-01-01T12:00:00+07:00, 12:00:00+07:00
 * */
function fixTimeZone(date) {
    const timeZone = getTimeZoneFromDateTimeStamp(date);
    if (!timeZone) {
        throw new Error(`Timezone offset not found in the string ${date}`);
    }
    const arr = timeZone.split(":");
    return replaceTimeZone(date, `${arr[0]}:${arr[1] || "00"}`);
}
exports.fixTimeZone = fixTimeZone;
/**
 * @description
 * Format string by persisting the time as provide in the string.
 * @param {string} timeStamp. e.g 2020-01-01T12:00:00+07
 * @param {string} toFormat. Valid format string @see https://date-fns.org/docs/format
 * @returns {string} formatted string.
 * */
function toZonedFormat(timeStamp, toFormat) {
    const sanitizedTimeStamp = fixTimeZone(timeStamp);
    const timeZone = guessTimezoneNameFromISODateOROffset(sanitizedTimeStamp);
    const timeStampWithoutZone = replaceTimeZone(sanitizedTimeStamp);
    const zonedStamp = (0, date_fns_tz_1.utcToZonedTime)(timeStampWithoutZone, "");
    return (0, date_fns_tz_1.format)(zonedStamp, toFormat, { timeZone });
}
exports.toZonedFormat = toZonedFormat;
/**
 * @description
 * Takes the date and time string and convert it to Server Format.
 * @param {string} date. yyyy-MM-dd
 * @param {string} time. HH:mm:ssxxx
 * @return {string} yyyy-MM-ddTHH:mm:ssxxx
 * */
function toServerTimeStamp(date, time) {
    const sanitizedTime = fixTimeZone(time);
    validateFormat(date, types_1.DateFormats.short, "date");
    validateFormat(sanitizedTime, types_1.DateFormats.time, "time");
    return `${date}T${sanitizedTime}`;
}
exports.toServerTimeStamp = toServerTimeStamp;
/**
 * @description
 * Takes the date and time string and convert it to Server Format Date Object.
 * @param {string} date. yyyy-MM-dd
 * @param {string} time. HH:mm:ssxxx
 * @return {Date}
 * */
function serverTimeStampToDate(date, time) {
    return new Date(toServerTimeStamp(date, time));
}
exports.serverTimeStampToDate = serverTimeStampToDate;
/**
 * @description
 * Takes a timeStamp and removes the seconds part leaving just hours and minutes.
 * @param {string} timeStamp e.g. 10:00:00+07 or 10:00:00
 * @return {string} e.g. 10:00
 * */
function getTimeStampWithoutSeconds(timeStamp) {
    const timeSplit = timeStamp === null || timeStamp === void 0 ? void 0 : timeStamp.split("+");
    return timeSplit === null || timeSplit === void 0 ? void 0 : timeSplit[0].slice(0, -3);
}
exports.getTimeStampWithoutSeconds = getTimeStampWithoutSeconds;
const getTimeInHourMinuteFormat = (time) => {
    let formatedTime = "-";
    if (typeof time === "string") {
        const timeInDateFormat = parsePGTimeToDate(replaceTimeZone(time, ""));
        formatedTime = (0, date_fns_1.format)(timeInDateFormat, "HH:mm");
    }
    return formatedTime;
};
exports.getTimeInHourMinuteFormat = getTimeInHourMinuteFormat;
const getTimeRange = (start, end) => {
    return `${(0, exports.getTimeInHourMinuteFormat)(start)}-${(0, exports.getTimeInHourMinuteFormat)(end)}`;
};
const getMonthDayYear = (date) => {
    return typeof date === "string"
        ? `${(0, date_fns_1.format)(new Date(date), "MMM")} ${(0, date_fns_1.format)(new Date(date), "dd")} ${(0, date_fns_1.format)(new Date(date), "yyyy")}`
        : "-";
};
function getDateAndTimeFilterFormat({ dateStart, timeStart, timeEnd, }) {
    const monthDate = getMonthDayYear(dateStart);
    const timeRange = getTimeRange(timeStart, timeEnd);
    return `${monthDate} • ${timeRange}`;
}
exports.getDateAndTimeFilterFormat = getDateAndTimeFilterFormat;
function getDateAndTimeDetail({ dateStart, timeStart, timeEnd, }) {
    const monthDate = getMonthDayYear(dateStart);
    const timeRange = getTimeRange(timeStart, timeEnd);
    return `${monthDate} • ${timeRange}`;
}
exports.getDateAndTimeDetail = getDateAndTimeDetail;
function getDayMonthYearNameWithCommas(date) {
    const isCorrectFormat = /\d{4}-\d{2}-\d{2}/.test(date);
    if (isCorrectFormat) {
        const dateObject = new Date(date);
        return `${(0, date_fns_1.format)(dateObject, "iii")}, ${(0, date_fns_1.format)(dateObject, "LLL")} ${(0, date_fns_1.format)(dateObject, "dd")}, ${(0, date_fns_1.format)(dateObject, "yyyy")}`;
    }
    return "-";
}
exports.getDayMonthYearNameWithCommas = getDayMonthYearNameWithCommas;
//# sourceMappingURL=dateUtils.js.map